/**
 * Utility functions for secure error handling and logging
 */

import { AxiosError } from 'axios';

/**
 * Safely logs errors in development mode without exposing sensitive data
 * In production, only logs minimal information
 *
 * @param error The error to log
 * @param context Optional context information about where the error occurred
 * @param details Additional details about the error context
 */
export function logError(
  error: unknown,
  context?: string,
  details?: string
): void {
  // Only log detailed errors in development
  if (import.meta.env.DEV) {
    // For Axios errors, extract useful information without sensitive data
    if (error instanceof AxiosError) {
      const safeError = {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        // Include error details from the response if available
        details: error.response?.data?.detail || error.response?.data?.message,
      };

      console.error(
        `API Error${context ? ` (${context})` : ''}${details ? ` - ${details}` : ''}:`,
        safeError
      );
      return;
    }

    // For other errors, log the message and stack but not the full error object
    if (error instanceof Error) {
      console.error(
        `Error${context ? ` (${context})` : ''}${details ? ` - ${details}` : ''}:`,
        {
          name: error.name,
          message: error.message,
          stack: error.stack,
        }
      );
      return;
    }

    // For unknown error types
    console.error(
      `Unknown error${context ? ` (${context})` : ''}${details ? ` - ${details}` : ''}:`,
      String(error)
    );
  } else {
    // In production, log minimal information
    if (error instanceof Error) {
      console.error(
        `Error occurred${context ? ` in ${context}` : ''}${details ? ` - ${details}` : ''}`
      );
    } else {
      console.error(
        `Unknown error occurred${context ? ` in ${context}` : ''}${details ? ` - ${details}` : ''}`
      );
    }
  }
}

/**
 * Extracts a user-friendly error message from an error object
 *
 * @param error The error to extract a message from
 * @param fallbackMessage A fallback message if no specific message can be extracted
 * @returns A user-friendly error message
 */
export function getErrorMessage(
  error: unknown,
  fallbackMessage = 'An error occurred'
): string {
  if (error instanceof AxiosError) {
    // Try to get a specific error message from the response
    const responseError =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      error.response?.data?.error;

    if (responseError) {
      return typeof responseError === 'string'
        ? responseError
        : JSON.stringify(responseError);
    }

    // Use HTTP status text if available
    if (error.response?.statusText) {
      return `${error.response.statusText}${error.response.status ? ` (${error.response.status})` : ''}`;
    }

    // Use the error message if available
    if (error.message && !error.message.includes('Network Error')) {
      return error.message;
    }

    // For network errors, provide a more user-friendly message
    if (error.message && error.message.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
  }

  // For regular Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // For unknown error types
  return fallbackMessage;
}

/**
 * Categorizes an error to determine appropriate user messaging
 *
 * @param error The error to categorize
 * @returns The error category
 */
export function categorizeError(
  error: unknown
): 'network' | 'auth' | 'validation' | 'server' | 'unknown' {
  if (error instanceof AxiosError) {
    // Network errors
    if (!error.response) {
      return 'network';
    }

    // Authentication errors
    if (error.response.status === 401 || error.response.status === 403) {
      return 'auth';
    }

    // Validation errors
    if (error.response.status === 400 || error.response.status === 422) {
      return 'validation';
    }

    // Server errors
    if (error.response.status >= 500) {
      return 'server';
    }
  }

  return 'unknown';
}

/**
 * Extracts field-specific errors from an API error response.
 * Django REST Framework returns errors in the format: { "errors": { "field_name": ["error message"] } }
 * or { "field_name": ["error message"] }
 * 
 * @param error The error to extract field errors from
 * @returns An object mapping field names to arrays of error messages, or null if no field errors
 */
export function getFieldErrors(
  error: unknown
): Record<string, string[]> | null {
  if (!(error instanceof AxiosError)) {
    return null;
  }

  const data = error.response?.data;
  if (!data) {
    return null;
  }

  // Check for nested errors object (Django format: { "detail": "...", "errors": { ... } })
  if (data.errors && typeof data.errors === 'object') {
    return normalizeFieldErrors(data.errors);
  }

  // Check for direct field errors (Django format: { "field_name": ["error"] })
  // Exclude known non-field keys
  const nonFieldKeys = ['detail', 'message', 'error', 'non_field_errors'];
  const fieldErrors: Record<string, string[]> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (!nonFieldKeys.includes(key) && Array.isArray(value)) {
      fieldErrors[key] = value.map(v => String(v));
    }
  }

  if (Object.keys(fieldErrors).length > 0) {
    return fieldErrors;
  }

  // Check for non_field_errors
  if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
    return { non_field_errors: data.non_field_errors };
  }

  return null;
}

/**
 * Normalize field errors to ensure all values are string arrays
 */
function normalizeFieldErrors(
  errors: Record<string, unknown>
): Record<string, string[]> {
  const normalized: Record<string, string[]> = {};
  
  for (const [key, value] of Object.entries(errors)) {
    if (Array.isArray(value)) {
      normalized[key] = value.map(v => String(v));
    } else if (typeof value === 'string') {
      normalized[key] = [value];
    } else if (value && typeof value === 'object') {
      // Handle nested errors (e.g., { "nested": { "field": ["error"] } })
      const nested = normalizeFieldErrors(value as Record<string, unknown>);
      for (const [nestedKey, nestedValue] of Object.entries(nested)) {
        normalized[`${key}.${nestedKey}`] = nestedValue;
      }
    }
  }
  
  return normalized;
}

/**
 * Gets a human-readable summary of all field errors
 */
export function getFieldErrorSummary(
  error: unknown
): string | null {
  const fieldErrors = getFieldErrors(error);
  if (!fieldErrors) {
    return null;
  }

  const messages: string[] = [];
  for (const [field, errors] of Object.entries(fieldErrors)) {
    const fieldName = field === 'non_field_errors' 
      ? '' 
      : `${formatFieldName(field)}: `;
    messages.push(`${fieldName}${errors.join(', ')}`);
  }

  return messages.join('. ');
}

/**
 * Format a field name for display (e.g., "first_name" -> "First name")
 */
function formatFieldName(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\./g, ' ')
    .replace(/^\w/, c => c.toUpperCase());
}
